/**
 * Agent Form Component
 * 
 * Form for creating and editing agents
 * - Zod validation schema
 * - React Hook Form integration
 * - Accessible form controls
 * - Error messages
 * - Loading states
 * 
 * @component
 * @example
 * <AgentForm
 *   mode="create"
 *   onSubmit={(data) => createAgent(data)}
 *   onCancel={() => setOpen(false)}
 * />
 */

import { FC, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FiAlertCircle } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import { 
  agentFormSchema, 
  type AgentFormProps, 
  type AgentFormData 
} from './AgentForm.types';
import styles from './AgentForm.module.css';

const AgentForm: FC<AgentFormProps> = ({
  initialValues,
  mode,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  // Initialize React Hook Form with Zod resolver
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<AgentFormData>({
    resolver: zodResolver(agentFormSchema),
    defaultValues: {
      organizationRef: initialValues?.organizationRef || '',
      name: initialValues?.name || '',
      identifier: initialValues?.identifier || '',
      hostname: initialValues?.hostname || '',
      machineId: initialValues?.machineId || '',
      os: initialValues?.os || '',
      osRelease: initialValues?.osRelease || '',
      osVersion: initialValues?.osVersion || '',
      architecture: initialValues?.architecture || '',
      pythonVersion: initialValues?.pythonVersion || '',
      agentVersion: initialValues?.agentVersion || '',
      status: initialValues?.status || 'offline',
    },
  });

  // Reset form when initialValues change
  useEffect(() => {
    if (initialValues) {
      reset({
        organizationRef: initialValues.organizationRef || '',
        name: initialValues.name || '',
        identifier: initialValues.identifier || '',
        hostname: initialValues.hostname || '',
        machineId: initialValues.machineId || '',
        os: initialValues.os || '',
        osRelease: initialValues.osRelease || '',
        osVersion: initialValues.osVersion || '',
        architecture: initialValues.architecture || '',
        pythonVersion: initialValues.pythonVersion || '',
        agentVersion: initialValues.agentVersion || '',
        status: initialValues.status || 'offline',
      });
    }
  }, [initialValues, reset]);

  /**
   * Handle form submission
   */
  const onSubmitForm = async (data: AgentFormData) => {
    await onSubmit(data);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmitForm)} noValidate>
      {/* Name Field - Readonly in edit mode */}
      <div className={styles.formGroup}>
        <label htmlFor="name" className={styles.label}>
          Nome do Agente {mode === 'create' && <span className={styles.required}>*</span>}
        </label>
        <input
          id="name"
          type="text"
          className={styles.input}
          {...register('name')}
          placeholder="Digite o nome do agente"
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'name-error' : undefined}
          disabled={isLoading || mode === 'edit'}
          readOnly={mode === 'edit'}
          autoFocus={mode === 'create'}
        />
        {errors.name && (
          <span id="name-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.name.message}
          </span>
        )}
      </div>

      {/* Organization Reference Field - Readonly in edit mode */}
      <div className={styles.formGroup}>
        <label htmlFor="organizationRef" className={styles.label}>
          Referência da Organização (UUID) {mode === 'create' && <span className={styles.required}>*</span>}
        </label>
        <input
          id="organizationRef"
          type="text"
          className={styles.input}
          {...register('organizationRef')}
          placeholder="00000000-0000-0000-0000-000000000000"
          aria-invalid={!!errors.organizationRef}
          aria-describedby={errors.organizationRef ? 'orgRef-error' : undefined}
          disabled={isLoading || mode === 'edit'}
          readOnly={mode === 'edit'}
        />
        {errors.organizationRef && (
          <span id="orgRef-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.organizationRef.message}
          </span>
        )}
      </div>

      {/* Identifier Field - Readonly in edit mode */}
      <div className={styles.formGroup}>
        <label htmlFor="identifier" className={styles.label}>
          Identificador
        </label>
        <input
          id="identifier"
          type="text"
          className={styles.input}
          {...register('identifier')}
          placeholder="Digite o identificador"
          aria-invalid={!!errors.identifier}
          aria-describedby={errors.identifier ? 'identifier-error' : undefined}
          disabled={isLoading || mode === 'edit'}
          readOnly={mode === 'edit'}
        />
        {errors.identifier && (
          <span id="identifier-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.identifier.message}
          </span>
        )}
      </div>

      {/* Hostname Field - ONLY EDITABLE FIELD */}
      <div className={styles.formGroup}>
        <label htmlFor="hostname" className={styles.label}>
          Hostname
        </label>
        <input
          id="hostname"
          type="text"
          className={styles.input}
          {...register('hostname')}
          placeholder="Digite o hostname"
          aria-invalid={!!errors.hostname}
          aria-describedby={errors.hostname ? 'hostname-error' : undefined}
          disabled={isLoading}
          autoFocus={mode === 'edit'}
        />
        {errors.hostname && (
          <span id="hostname-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.hostname.message}
          </span>
        )}
      </div>

      {/* Status Field - Readonly in edit mode */}
      <div className={styles.formGroup}>
        <label htmlFor="status" className={styles.label}>
          Status
        </label>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <select
              id="status"
              className={styles.select}
              {...field}
              disabled={isLoading || mode === 'edit'}
            >
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="inactive">Inativo</option>
              <option value="error">Erro</option>
            </select>
          )}
        />
        {errors.status && (
          <span className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.status.message}
          </span>
        )}
      </div>

      {/* Divider */}
      <div className={styles.divider}>
        <span className={styles.dividerText}>Informações do Sistema</span>
      </div>

      {/* OS Field - Readonly in edit mode */}
      <div className={styles.formGroup}>
        <label htmlFor="os" className={styles.label}>
          Sistema Operacional
        </label>
        <input
          id="os"
          type="text"
          className={styles.input}
          {...register('os')}
          placeholder="Linux, Windows, macOS"
          aria-invalid={!!errors.os}
          disabled={isLoading || mode === 'edit'}
          readOnly={mode === 'edit'}
        />
      </div>

      {/* Architecture Field - Readonly in edit mode */}
      <div className={styles.formGroup}>
        <label htmlFor="architecture" className={styles.label}>
          Arquitetura
        </label>
        <input
          id="architecture"
          type="text"
          className={styles.input}
          {...register('architecture')}
          placeholder="x86_64, arm64"
          aria-invalid={!!errors.architecture}
          disabled={isLoading || mode === 'edit'}
          readOnly={mode === 'edit'}
        />
      </div>

      {/* Python Version Field - Readonly in edit mode */}
      <div className={styles.formGroup}>
        <label htmlFor="pythonVersion" className={styles.label}>
          Versão do Python
        </label>
        <input
          id="pythonVersion"
          type="text"
          className={styles.input}
          {...register('pythonVersion')}
          placeholder="3.11.0"
          aria-invalid={!!errors.pythonVersion}
          disabled={isLoading || mode === 'edit'}
          readOnly={mode === 'edit'}
        />
      </div>

      {/* Agent Version Field - Readonly in edit mode */}
      <div className={styles.formGroup}>
        <label htmlFor="agentVersion" className={styles.label}>
          Versão do Agente
        </label>
        <input
          id="agentVersion"
          type="text"
          className={styles.input}
          {...register('agentVersion')}
          placeholder="1.0.0"
          aria-invalid={!!errors.agentVersion}
          disabled={isLoading || mode === 'edit'}
          readOnly={mode === 'edit'}
        />
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
        >
          {mode === 'create' ? 'Criar Agente' : 'Salvar Alterações'}
        </Button>
      </div>
    </form>
  );
};

export default AgentForm;
